import { useState, useCallback, useEffect } from 'react';
import { UrlRecommendationContent } from '../type';
import { useEditedWidget } from 'features/Rooms/PersonalMeetingCorner/states/hooks';

export interface LinkData {
    id: string;
    url: string;
    title: string;
    description?: string;
    image?: string;
    favicon?: string;
    siteName?: string;
    type?: string;
}

export interface URLState {
    links: LinkData[];
    newUrl: string;
    isAdding: boolean;
    addLink: (url: string) => void;
    removeLink: (id: string) => void;
    reorderLinks: (fromIndex: number, toIndex: number) => void;
    setNewUrl: (url: string) => void;
    normalizeUrl: (url: string) => string;
    fetchLinkPreview: (url: string) => Promise<LinkData>;
}

export const useURLState = (onURLSelect: (content: UrlRecommendationContent) => void): URLState => {
    const [links, setLinks] = useState<LinkData[]>([]);
    const [newUrl, setNewUrl] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [editedWidget] = useEditedWidget();
    const isEditMode = editedWidget?.type === 'urls' && editedWidget?.content?.links?.length > 0;

    const normalizeUrl = useCallback((url: string): string => {
        const trimmedUrl = url.trim();
        if (!trimmedUrl) return '';

        // Add https:// if missing
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
            return `https://${trimmedUrl}`;
        }
        return trimmedUrl;
    }, []);

    const fetchLinkPreview = useCallback(
        async (url: string): Promise<LinkData> => {
            const normalizedUrl = normalizeUrl(url);
            const domain = new URL(normalizedUrl).hostname;

            // Simulate OpenGraph data fetching
            // In production, this would make actual API calls to fetch metadata
            const mockData: { [key: string]: Partial<LinkData> } = {
                'github.com': {
                    // eslint-disable-next-line prettier/prettier
                    title: 'GitHub: Let\'s build from here',
                    description: 'GitHub is where over 100 million developers shape the future of software.',
                    favicon: 'https://github.com/favicon.ico',
                    siteName: 'GitHub',
                    type: 'website'
                },
                'youtube.com': {
                    title: 'YouTube',
                    description: 'Share your videos with friends, family, and the world.',
                    favicon: 'https://www.youtube.com/favicon.ico',
                    siteName: 'YouTube',
                    type: 'video'
                },
                'linkedin.com': {
                    title: 'LinkedIn: Log In or Sign Up',
                    description: '500 million+ members | Manage your professional identity.',
                    favicon: 'https://www.linkedin.com/favicon.ico',
                    siteName: 'LinkedIn',
                    type: 'website'
                },
                'stackoverflow.com': {
                    title: 'Stack Overflow - Where Developers Learn, Share, & Build Careers',
                    description: 'Stack Overflow is the largest, most trusted online community for developers.',
                    favicon: 'https://stackoverflow.com/favicon.ico',
                    siteName: 'Stack Overflow',
                    type: 'website'
                }
            };

            const domainKey = domain.replace('www.', '');
            const fallbackData: LinkData = {
                id: crypto.randomUUID(),
                url: normalizedUrl,
                title: domain,
                description: `Visit ${domain}`,
                favicon: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
                // favicon: `https://${domain}/favicon.ico`,
                siteName: domain,
                type: 'website'
            };

            return {
                ...fallbackData,
                ...mockData[domainKey]
            };
        },
        [normalizeUrl]
    );

    const addLink = useCallback(
        async (url: string) => {
            if (!url.trim() || links.length >= 10) return;

            setIsAdding(true);
            try {
                const linkData = await fetchLinkPreview(url);
                const newLinks = [...links, linkData];
                setLinks(newLinks);
                setNewUrl('');

                // Update parent component
                onURLSelect({ links: newLinks });
            } catch (error) {
                console.error('Failed to add link:', error);
            } finally {
                setIsAdding(false);
            }
        },
        [links, fetchLinkPreview, onURLSelect]
    );

    const removeLink = useCallback(
        (id: string) => {
            const newLinks = links.filter((link) => link.id !== id);
            setLinks(newLinks);
            onURLSelect({ links: newLinks });
        },
        [links, onURLSelect]
    );

    const reorderLinks = useCallback(
        (fromIndex: number, toIndex: number) => {
            const newLinks = [...links];
            const [movedLink] = newLinks.splice(fromIndex, 1);
            newLinks.splice(toIndex, 0, movedLink);
            setLinks(newLinks);
            onURLSelect({ links: newLinks });
        },
        [links, onURLSelect]
    );

    useEffect(() => {
        if (isEditMode) {
            setLinks(editedWidget?.content?.links);
        }
    }, [editedWidget?.content?.links]);

    return {
        links,
        newUrl,
        isAdding,
        addLink,
        removeLink,
        reorderLinks,
        setNewUrl,
        normalizeUrl,
        fetchLinkPreview
    };
};
